/**
 * Donna como aplicación instalable: registra el service worker, guarda el aviso de instalación del navegador
 * (Chrome, Edge, Android) y detecta en qué equipo estamos para mostrar los pasos correctos (iPhone/iPad no tienen botón).
 */
import { useEffect, useState } from "react";

interface AvisoInstalacion extends Event { prompt(): Promise<void>; userChoice: Promise<{ outcome: "accepted" | "dismissed" }> }

let aviso: AvisoInstalacion | null = null;
let recienInstalada = false;
const oyentes = new Set<() => void>();
const avisar = () => oyentes.forEach((f) => f());

const enCapacitor = () => typeof window !== "undefined" && "Capacitor" in window;

export function iniciarPwa() {
  if (typeof window === "undefined" || enCapacitor()) return;
  addEventListener("beforeinstallprompt", (e) => { e.preventDefault(); aviso = e as AvisoInstalacion; avisar(); });
  addEventListener("appinstalled", () => { aviso = null; recienInstalada = true; avisar(); });
  const local = location.hostname === "localhost" || location.hostname === "127.0.0.1";
  if ("serviceWorker" in navigator && (location.protocol === "https:" || local)) {
    addEventListener("load", () => { navigator.serviceWorker.register("/sw.js").catch(() => undefined); });
  }
}

export type Equipo = "instalada" | "boton" | "iphone" | "ipad" | "mac-safari" | "android" | "otro";

/** Equipo y navegador actuales, para saber si hay botón de instalar o hay que dar pasos a mano. */
export function equipoActual(): Equipo {
  if (enCapacitor()) return "instalada";
  const standalone = matchMedia?.("(display-mode: standalone)").matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
  if (standalone || recienInstalada) return "instalada";
  if (aviso) return "boton";
  const ua = navigator.userAgent;
  if (/iPhone|iPod/.test(ua)) return "iphone";
  // el iPad con iPadOS se presenta como Mac, pero tiene pantalla táctil
  if (/iPad/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)) return "ipad";
  if (/Macintosh/.test(ua) && /Safari/.test(ua) && !/Chrome|Chromium|Edg|Firefox/.test(ua)) return "mac-safari";
  if (/Android/.test(ua)) return "android";
  return "otro";
}

/** Para los componentes: el equipo actual (se actualiza si el navegador ofrece instalar) y la acción de instalar. */
export function useInstalar() {
  const [equipo, setEquipo] = useState<Equipo>(equipoActual);
  useEffect(() => { const f = () => setEquipo(equipoActual()); oyentes.add(f); f(); return () => { oyentes.delete(f); }; }, []);
  async function instalar(): Promise<boolean> {
    if (!aviso) return false;
    const a = aviso;
    await a.prompt();
    const r = await a.userChoice.catch(() => ({ outcome: "dismissed" as const }));
    aviso = null;
    if (r.outcome === "accepted") recienInstalada = true;
    avisar();
    return true;
  }
  return { equipo, instalar };
}

/** true mientras hay internet. */
export function useEnLinea() {
  const [enLinea, setEnLinea] = useState(() => (typeof navigator === "undefined" ? true : navigator.onLine));
  useEffect(() => {
    const si = () => setEnLinea(true), no = () => setEnLinea(false);
    addEventListener("online", si); addEventListener("offline", no);
    return () => { removeEventListener("online", si); removeEventListener("offline", no); };
  }, []);
  return enLinea;
}

/** Huella de la versión que está abierta (la de su propio archivo app-XXXX.js); vacía si no hay (desarrollo o app móvil). */
const miVersion = () => document.querySelector<HTMLScriptElement>('script[src*="/app-"]')?.getAttribute("src")?.match(/app-([A-Z0-9]+)\.js/i)?.[1] ?? "";

/**
 * true cuando el servidor ya publicó una versión más nueva que la abierta en esta pantalla.
 * Se revisa al abrir, al volver a la pestaña o a la app, al recuperar internet y cada 5 minutos. No recarga sola: así nadie pierde lo que está escribiendo.
 */
export function useVersionNueva(): boolean {
  const [nueva, setNueva] = useState(false);
  useEffect(() => {
    const mia = miVersion();
    if (!mia || enCapacitor()) return;
    let vivo = true;
    async function revisar() {
      if (!navigator.onLine || document.visibilityState === "hidden") return;
      try {
        const r = await fetch(`/index.html?v=${Date.now()}`, { cache: "no-store" });
        if (!r.ok) return;
        const h = (await r.text()).match(/\/app-([A-Z0-9]+)\.js/i)?.[1];
        if (vivo && h && h !== mia) setNueva(true);
      } catch { /* sin conexión: se vuelve a intentar más tarde */ }
    }
    const t0 = setTimeout(revisar, 15000);
    const cada = setInterval(revisar, 5 * 60 * 1000);
    const f = () => { void revisar(); };
    addEventListener("online", f); document.addEventListener("visibilitychange", f);
    return () => { vivo = false; clearTimeout(t0); clearInterval(cada); removeEventListener("online", f); document.removeEventListener("visibilitychange", f); };
  }, []);
  return nueva;
}
